import React, { useEffect, useState } from "react";
import { ItemInLegend } from "./ItemInLegend";
import { ResizeableContainer } from "./ResizeableContainer";
import { Resizeable } from "./Resizeable";
import { DivAutoSizeFromHereToBottom } from "./DivAutoSizeFromHereToBottom";
import { RightSide } from "./RightSide";
import LeftSize from "./LeftSide";
import Tooltip from "src/components/tooltip";
import { graphIcon } from "src/components/icons-svg";

import {
  leftDefaultWidth,
  leftSizeMinWidth,
  resourceTabList,
} from "./sc-analyzer-frontend-data";
import { invalidateCache, requestJSON } from "../utils/request-module";
import Progress from "../components/progress";
import store from "src/store";
import consts from "shared/consts";
import useFeature from "src/utils/subscriptionWrapper";
import ConfirmationModal from "src/components/confirmation-modal";
import { delay } from "src/utils/react-utils";
import Dialog from "src/components/dialog";
import OneSimulation from "src/app-components/one-simulation";
import { useRefreshComponent } from "../utils/use-refresh-component";
import { findResource } from "./CardElement";
import { scrollIntoView } from "./gotoAST";

function qs() {
  return require("qs").parse(location.search.slice(1));
}

export default function SCAnalyzer() {
  const id = qs().review_id;
  const REVIEW_TOOL_ALLOW_PRIVATE_SIMULATIONS = useFeature(
    consts.FEATURE_FLAG_REVIEW_TOOL_ALLOW_PRIVATE_SIMULATIONS
  );

  const [selectedFile, set_selectedFile] = useState("MainContract.sol");
  const [selectedResourseType, set_selectedResourseType] = useState(
    resourceTabList[0].name
  );
  const [selectedResource, set_selectedResource] = useState(null);
  const [json, setJSON] = useState(null);
  const [solFiles, setSOLFiles] = useState([]);
  const [mainFile, set_mainFile] = useState("");
  const [completedTasks, set_completedTasks] = useState({});
  const [openSimulationTestModal, setOpenSimulationTestModal] = useState(false);
  const [testSimulationId, setTestSimulationId] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [openInitializationStepsModal, setOpenInitializationStepsModal] =
    useState(false);
  const [defaultSimulationId, setDefaultSimulationId] = useState(null);
  const [showArchitecture, setShowArchitecture] = useState(false);
  const [showAI, set_showAI] = useState(false);
  const [graphData, setGraphData] = useState([]);

  useEffect(() => {
    if (id) {
      getSelectedReviewData();
      getAnalyzedData();
    } else {
      getDemoAnalyzedData();
    }
    getTasks();
    setShowArchitecture(qs().show_architecture === "true");
  }, []);

  useEffect(() => {
    if (json) {
      getTotalProgress();
    }
  }, [json, completedTasks]);

  const getAnalyzedData = async () => {
    try {
      const url = `/review/my-review/${id}`;
      const reviewData = await requestJSON("GET", url, undefined, true);
      if (reviewData) {
        reviewData.analyzedData.json.tabsObject = {};

        reviewData.analyzedData.json.tabsArray.forEach((item) => {
          reviewData.analyzedData.json.tabsObject[item.id] = item;
        });

        setJSON(reviewData.analyzedData.json);
        setSOLFiles(reviewData.analyzedData.solFiles);
        set_mainFile(reviewData.analyzedData.mainFile);
        set_selectedFile(reviewData.analyzedData.mainFile);
        setGraphData(reviewData.analyzedData.graphData);
      }
    } catch (err) {
      console.error("Error fetching review data:", err);
    }
  };

  const getDemoAnalyzedData = async () => {
    const { json, solFiles, mainFile } = await requestJSON("GET", "/demo-json");

    setJSON(json);
    setSOLFiles(solFiles);
    set_mainFile(mainFile);
    set_selectedFile(mainFile);
  };

  const getTasks = async () => {
    const taskReviewId = id || "50a";
    try {
      const url = `/get-tasks?taskReviewId=${taskReviewId}`;
      const tasks = await requestJSON("POST", url);
      if (tasks && tasks.completedTasks) {
        set_completedTasks(tasks.completedTasks);
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
    }
  };

  const saveTasks = async () => {
    const taskReviewId = id || "50a";
    try {
      const url = `/save-tasks?taskReviewId=${taskReviewId}`;
      await requestJSON("POST", url, completedTasks);
    } catch (err) {
      console.error("Error saving tasks:", err);
    }
  };

  const getTotalProgress = () => {
    if (!json) {
      return { total: 0, completed: 0 };
    }
    const totalTasks = deepSearchJSON(json.tabsArray, "type", "task");
    const completedTasksCount = Object.values(completedTasks).filter(
      (value) => value === true
    ).length;

    return {
      total: totalTasks.length,
      completed: completedTasksCount,
    };
  };

  const getProgressForTab = (tabId) => {
    if (!json) {
      return { total: 0, completed: 0 };
    }

    const tabJson = json.tabsObject[tabId];
    const totalTabTasks = tabJson ? deepSearchJSON(tabJson, "type", "task") : [];

    const elements = json.tabsObject[tabId]?.elements || [];
    const completedTaskSet = new Set();

    elements.forEach((element) => {
      if (element.inUse?.length > 0 || !element.hasOwnProperty("inUse")) {
        element.cards.forEach((card) => {
          card.cardElements.forEach((cardElement) => {
            if (cardElement.type === "task") {
              const key = `${card.id}_${cardElement.id}`;
              if (completedTasks[key] === true) {
                completedTaskSet.add(key);
              }
            }
          });
        });
      }
    });

    return {
      total: totalTabTasks.length,
      completed: completedTaskSet.size,
    };
  };

  const getProgressForResource = (astId) => {
    if (!json) {
      return { total: 0, completed: 0 };
    }

    const resourceJson = json.tabsObject[selectedResourseType]?.elements.find(
      (x) => x.astId === astId
    );
    const totalTabTasks = deepSearchJSON(resourceJson, "type", "task");

    const elements = json.tabsObject[selectedResourseType]?.elements.filter(
      (element) => element.astId === astId
    );
    const completedTaskSet = new Set();

    elements.forEach((element) => {
      if (element.inUse?.length > 0 || !element.hasOwnProperty("inUse")) {
        element.cards.forEach((card) => {
          card.cardElements.forEach((cardElement) => {
            if (cardElement.type === "task") {
              const key = `${card.id}_${cardElement.id}`;
              if (completedTasks[key] === true) {
                completedTaskSet.add(key);
              }
            }
          });
        });
      }
    });

    return {
      total: totalTabTasks.length,
      completed: completedTaskSet.size,
    };
  };

  const deepSearchJSON = (obj = {}, key, value) => {
    const result = [];
    const recursiveSearch = (obj = {}) => {
      if (!obj || typeof obj !== "object") {
        return;
      }
      if (obj[key] === value) {
        result.push(obj);
      }
      Object.keys(obj).forEach((k) => {
        recursiveSearch(obj[k]);
      });
    };
    recursiveSearch(obj);
    return result;
  };

  // =======store used start=======

  const getSelectedReviewData = async () => {
    if (!Object.keys(store.selectedReviewData).length) {
      store.selectedReviewData = await store.fetchOneReview(id);
      store.updateStore();
    }
  };
  const showEditReviewFileModal = async () => {
    if (id) {
      store.showEditReviewFileModal(id);
    }
  };

  const onDefineDefaultStepClick = async () => {
    if (id) {
      if (store.selectedReviewData.defaultStepSimulationId) {
        setDefaultSimulationId(
          store.selectedReviewData.defaultStepSimulationId
        );
        setOpenInitializationStepsModal(true);
      } else {
        const newSimulationId = await store.newSimulation(
          {
            name: "Default simulation",
            public: !REVIEW_TOOL_ALLOW_PRIVATE_SIMULATIONS,
            engagement_id: store.selectedReviewData.engagementData._id,
            review_id: store.selectedReviewData._id,
          },
          null,
          false,
          true
        );
        const reqBody = {
          simulationId: newSimulationId,
          reviewId: store.selectedReviewData._id,
        };
        store.selectedReviewData.defaultStepSimulationId = newSimulationId;
        store.updateStore();
        store.addDefaultStepSimulationId(reqBody);
        setDefaultSimulationId(
          store.selectedReviewData.defaultStepSimulationId
        );
        setOpenInitializationStepsModal(true);
      }
    }
  };

  const reAnalyze = async () => {
    setAnalyzing(true);

    setJSON(null);
    setSOLFiles([]);
    set_mainFile("");
    set_selectedFile("MainContract.sol");
    setGraphData([]);

    await store.analyze(id);
    await store.updateMyReviewList();
    const url = `/review/my-review/${id}`;
    invalidateCache("GET", url, undefined);
    store.selectedReviewData = {};
    await getAnalyzedData();
    setAnalyzing(false);
    store.closeReanalyzeConfirmationModal();
  };

  store.useRerenderIfChange(() => [store.showReanalyzeConfirmationModal]);

  const checkSimulationExistsWithTaskId = async (task_id) => {
    try {
      const body = {
        task_id: task_id,
        review_id: id,
      };
      const url = "/simulation/isSimulationExists";
      const simulationData = await requestJSON("POST", url, body, false);
      return simulationData;
    } catch (err) {
      console.error(
        "Error checking simulation data with task id:",
        err
      );
    }
  };

  const testSimulation = async (task_id, title) => {
    const simulationData = await checkSimulationExistsWithTaskId(task_id);
    if (simulationData) {
      setTestSimulationId(simulationData._id);
      setOpenSimulationTestModal(true);
    } else {
      createSimulationForTest(task_id, title);
    }
  };

  const createSimulationForTest = async (task_id, title) => {
    store.creatingNewSimulation = true;
    store.updateStore();
    await delay(400);
    const simulationId = await store.newSimulation(
      {
        name: title,
        public: !REVIEW_TOOL_ALLOW_PRIVATE_SIMULATIONS,
        engagement_id: store.selectedReviewData.engagement_id,
        review_id: store.selectedReviewData._id,
        task_id: task_id,
      },
      null,
      false,
      false
    );
    store.creatingNewSimulation = false;
    store.updateStore();
    setTestSimulationId(simulationId);
    setOpenSimulationTestModal(true);
  };

  const onSimulationTestModalClose = () => {
    setTestSimulationId(null);
    setOpenSimulationTestModal(false);
  };

  const onInitializationStepsModalClose = () => {
    setOpenInitializationStepsModal(false);
    setDefaultSimulationId(null);
  };
  // =======store used end=======
  const refresh = useRefreshComponent();

  const [isEditingFiles, set_isEditingFiles] = useState(false);

  /** @type {scState}*/
  const scState = {
    json,
    solFiles,
    graphData,
    mainFile,
    selectedFile,
    completedTasks,
    analyzing,
    set_completedTasks,
    set_selectedFile,
    selectedResource,
    saveTasks,
    showAI,
    set_showAI,
    goto: (file, line) => {
      set_selectedFile(file);
      if (line) {
        setTimeout(() => {
          const elm = document.querySelector(`[data-line="${line}"]`);
          if (elm) {
            elm.style.border = "1px solid yellow";
            setTimeout(() => {
              elm.style.border = "";
            }, 3000);
            scrollIntoView(elm);
          }
        });
      }
    },
    set_selectedResource: (newResource) => {
      set_selectedResource(newResource);
      set_selectedResourseType(newResource.gotoTab);
    },
    print: () => {
      let str =
        "<h1>Comprehensive Smart Contract Security Analysis and Task Completion Report</h1>";
      str += "<hr>";
      for (const t of scState.json.tabsArray) {
        str += `<h1>${t.name}</h1>\n<ul>`;
        for (const e of t.elements) {
          str += `<li><h2>${e.name}</h2>\n<ul>`;
          for (const c of e.cards) {
            str += `<li><h3>${c.title}</h3>\n`;
            for (const ce of c.cardElements) {
              if (ce.type === "table") continue;
              if (ce.type === "expandable-path-resources") {
                str += `<ul>${ce.resources.map((x) => x.name).join(", ")}</ul>`;
                continue;
              }
              if (ce.type === "resources") {
                str += `<ul>${ce.resources
                  .map((x) => x.displayName || x.name)
                  .join(", ")}</ul>`;
                continue;
              }
              if (ce.type === "resources-flattened") {
                str += ce.astIDs
                  .map((x) => findResource({ scState, astId: x }).name)
                  .join(", ");
                continue;
              }
              str += "<ul>";
              if (ce.type === "task") {
                str += `<li>${ce.title}<b>${
                  scState.completedTasks[ce.id] ? "Done" : "Not Done"
                }</b></li>`;
              }
              str += "</ul>";
            }
          }
          str += "</ul></li>";
        }
        str += "</ul>";
      }
      document.body.innerHTML = str;
    },
    selectedResourseType,
    set_selectedResourseType: (newType) => {
      set_selectedResourseType(newType);
      set_selectedResource(null);
    },
    set_isEditingFiles,
    isEditingFiles,
    getTotalProgress,
    getProgressForTab,
    getProgressForResource,
    showEditReviewFileModal,
    onDefineDefaultStepClick,
    reAnalyze,
    testSimulation,
    showArchitecture,
    toogleArchitectureRoute: (showArch) => {
      setShowArchitecture(showArch);
      const url = new URL(window.location.href);
      const queryParams = require("qs").parse(url.search, {
        ignoreQueryPrefix: true,
      });

      queryParams.show_architecture = showArch;

      const newQueryString = require("qs").stringify(queryParams, {
        addQueryPrefix: true,
      });

      const newUrl = `${url.origin}${url.pathname}${newQueryString}`;

      window.history.pushState({}, "", newUrl);
    },
  };

  useEffect(() => {
    const newState = {
      selectedResource,
      selectedResourseType,
    };
    if (_.isEqual(newState, history.state)) {
      return;
    }
    history.pushState(newState, "");
  }, [selectedResource, selectedResourseType]);

  useEffect(() => {
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  function onPopState({ state }) {
    const { selectedResourseType, selectedResource } = state;
    if (selectedResourseType) {
      set_selectedResourseType(selectedResourseType);
    }
    if (selectedResource) {
      set_selectedResource(selectedResource);
    }
  }

  window.scState = scState;

  if (!json)
    return (
      <div>
        <br />
        <Progress time={500} />
      </div>
    );

  return (
    <>
      <ConfirmationModal
        maxWidth="sm"
        title="Re-analyze"
        message={"Do you want to re-analyze?"}
        open={store.showReanalyzeConfirmationModal}
        onClose={() => {
          store.closeReanalyzeConfirmationModal();
        }}
        confirmationBtnText="Re-analyze"
        onConfirm={() => {
          reAnalyze();
        }}
        loading={analyzing}
      />

      {openSimulationTestModal && (
        <Dialog
          open={openSimulationTestModal}
          children={
            <OneSimulation
              simulationId={testSimulationId}
              onClose={() => {
                onSimulationTestModalClose();
              }}
            />
          }
          zIndex={1202}
        ></Dialog>
      )}

      {openInitializationStepsModal && (
        <Dialog
          open={openInitializationStepsModal}
          children={
            <OneSimulation
              simulationId={defaultSimulationId}
              onClose={() => {
                onInitializationStepsModalClose();
              }}
            />
          }
          zIndex={1202}
        ></Dialog>
      )}

      <div
        css={`
          background-color: #f6f6f6;
        `}
      >
        <DivAutoSizeFromHereToBottom>
          <ResizeableContainer>
            <Resizeable
              minWidth={30}
              maxWidth={1000}
              name="sc-analyzer-left-width"
              defaultWidth={leftDefaultWidth}
            >
              <div
                css={`
                  min-width: ${leftSizeMinWidth}px;
                  overflow: scroll;
                  display: initial;
                `}
              >
                <LeftSize scState={scState} />
              </div>
            </Resizeable>
            <RightSide scState={scState} />
          </ResizeableContainer>
        </DivAutoSizeFromHereToBottom>
      </div>
      <div
        style={{ position: "absolute", right: 10, bottom: 10, display: "flex" }}
      >
        {window.config.SHOW_ARCHITECTURE === "YES" && (
          <>
            <div
              onClick={() => {
                scState.toogleArchitectureRoute(true);
              }}
              css={`
                font-size: 16px;
                font-weight: 400;
                white-space: nowrap;
                background-color: white;
                cursor: pointer;
                margin-top: 10px;
                margin-right: 10px;
                border: 1px solid skyblue;
              `}
              key="graph_icon_1"
            >
              <div
                css={`
                  padding: 10px 8px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `}
              >
                {graphIcon()}
              </div>
            </div>
            <div
              onClick={() => {
                scState.set_showAI(true);
              }}
              css={`
                font-size: 16px;
                font-weight: 400;
                white-space: nowrap;
                background-color: white;
                cursor: pointer;
                margin-top: 10px;
                border: 1px solid skyblue;
              `}
              key="graph_icon_2"
            >
              <div
                css={`
                  padding: 10px 8px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `}
              >
                AI
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
